import type { RpmPickList } from '@on3/api';
import type { IPredictionCenterPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import type { ErrorData } from '@on3/ui-lib/api/schema/custom-contracts';
import { useSite } from '@on3/ui-lib/index';
import { externalApi } from '@on3/ui-lib/utils/api';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { type AxiosError } from 'axios';
import clsx from 'clsx';
import { PrimaryButton } from 'components/UI/Buttons/PrimaryButton';
import { Text } from 'components/UI/Typography/Text';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import { PredictionCenterFilters } from './PredictionCenterFilters';
import { PredictionCenterItem } from './PredictionCenterItem';
import styles from './PredictionCenterList.module.scss';
import { PredictionCenterListHeader } from './PredictionCenterListHeader';
import { PredictionCenterPercentages } from './PredictionCenterPercentages';

export const PredictionCenterList = ({
  title,
  predictions,
  filters,
}: IPredictionCenterPageProps) => {
  const router = useRouter();
  const { currentSite, sportsData } = useSite();
  const orgKey = currentSite?.organizationKey;
  const name =
    currentSite?.organization?.knownAs ?? currentSite?.organization?.name;

  const { sports, years, teams } = filters;
  const availableYears = years.filter((yr) => yr >= 2022);
  const { sport, year } = router.query;
  const showPredictions = !!predictions?.list?.length;
  const dailyTitle = `${year} ${
    (!currentSite?.isNational && name) || ''
  } Picks`;
  const allTimeTitle = `All-Time ${
    (!currentSite?.isNational && name) || ''
  } Picks`;
  const [pageNumber, setPageNumber] = useState(2);
  const page = pageNumber;
  const [pagination, setPagination] = useState(predictions?.pagination);
  const [teamList, setTeamList] = useState<RpmPickList[]>(
    predictions?.list || [],
  );
  const [disable, setDisable] = useState(false);
  const showLoadMoreButton =
    pagination?.count && teamList && teamList.length < pagination?.count;

  const {
    all: allTimePredictionAccuracy,
    year: currentYearsPredictionAccuracy,
  } = predictions?.relatedModel || {};
  const hasCurrentYearsPredictionAccuracy = Object.values(
    currentYearsPredictionAccuracy || {},
  )?.length;
  const hasAllTimePredictionAccuracy = Object.values(
    allTimePredictionAccuracy || {},
  )?.length;
  const handleChange: React.MouseEventHandler = useCallback(async () => {
    setDisable(true);
    const sportKey = sportsData?.find(
      (sp) => sp?.name && slugify(sp?.name) === sport,
    )?.key;
    const params = {
      sportKey,
      orgKey,
      year: year === 'all' ? null : year,
      page,
    };

    try {
      const response = await externalApi.get(
        `/public/rdb/v1/recruitments/latest-rpm-picks`,
        {
          params,
        },
      );

      setTeamList([...teamList, ...response.data.list]);

      setPageNumber(pageNumber + 1);
      setPagination(response.data.pagination);
    } catch (e: unknown) {
      const { response, message }: AxiosError<ErrorData> =
        e as AxiosError<ErrorData>;

      console.error(
        'Expert Predictions Error: ',
        response?.data?.message || message,
      );
    } finally {
      setDisable(false);
    }
  }, [orgKey, page, pageNumber, sport, sportsData, teamList, year]);

  return (
    <section>
      <header>
        <Text className={styles.pageTitle} variant="h1">
          {title}
        </Text>
      </header>
      <PredictionCenterFilters
        sports={sports}
        teams={teams}
        years={availableYears}
      />
      <div className={styles.percentageContainer}>
        {!!hasCurrentYearsPredictionAccuracy && year !== 'all' && (
          <PredictionCenterPercentages
            accuracy={currentYearsPredictionAccuracy}
            percentageClassName={styles.currentYearsPrediction}
            percentageTitle={dailyTitle}
          />
        )}
        {!!hasAllTimePredictionAccuracy && (
          <PredictionCenterPercentages
            accuracy={allTimePredictionAccuracy}
            percentageTitle={allTimeTitle}
          />
        )}
      </div>
      {showPredictions && (
        <section>
          <PredictionCenterListHeader />
          <ul className={styles.predictionListWrapper}>
            {teamList?.map((prediction, index) => {
              return (
                <PredictionCenterItem
                  {...prediction}
                  key={`${prediction.player?.key}-${index}`}
                />
              );
            })}
          </ul>
        </section>
      )}
      {!!showLoadMoreButton && (
        <PrimaryButton
          className={styles.btnLoadMore}
          disabled={disable}
          fullWidth={false}
          onClick={handleChange}
          variant="text"
        >
          <Text
            className={clsx(styles.txtLoadMore, {
              [styles.isDisabled]: disable,
            })}
            color="primary"
            variant="subtitle1"
          >
            Load More
          </Text>
        </PrimaryButton>
      )}
      {!showPredictions && (
        <Text className={styles.noPredictions}>
          There are no predictions for the current selection.
        </Text>
      )}
    </section>
  );
};
